import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Default TavPlans branding
const defaultBranding = {
  tenant_id: 'default',
  company_name: 'TavPlans',
  logo_url: null,
  favicon_url: null,
  primary_color: '#1f2937',
  secondary_color: '#3b82f6',
  accent_color: '#10b981',
  font_family: 'Inter',
  custom_css: null,
  hide_footer_branding: false,
  custom_login_background: null,
  is_whitelabeled: false
};

const BrandingContext = createContext({
  branding: defaultBranding,
  loading: true,
  isWhitelabeled: false,
  refreshBranding: () => {}
});

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};

/**
 * Extract subdomain from hostname
 * Examples:
 * - acme.tavplans.app -> acme
 * - www.tavplans.app -> null (www is not a tenant)
 * - tavplans.app -> null
 * - localhost:3000 -> null (check URL params)
 */
const extractSubdomain = (hostname) => {
  // Always check URL params first (for testing in any environment)
  const urlParams = new URLSearchParams(window.location.search);
  const subdomainParam = urlParams.get('subdomain');
  if (subdomainParam) {
    return subdomainParam;
  }
  
  // Skip subdomain extraction for localhost
  if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
    return null;
  }
  
  // Split hostname
  const parts = hostname.split('.');
  
  // For subdomains like acme.tavplans.app
  // We need at least 3 parts: subdomain.domain.tld
  if (parts.length >= 3) {
    const subdomain = parts[0];
    // Ignore common non-tenant subdomains
    const ignoredSubdomains = ['www', 'app', 'api', 'admin', 'staging', 'preview', 'feature-gateway'];
    if (!ignoredSubdomains.includes(subdomain.toLowerCase())) {
      return subdomain;
    }
  }
  
  return null;
};

/**
 * Check if hostname is a custom domain (not a subdomain of our main domain)
 */
const isCustomDomain = (hostname) => {
  const mainDomains = [
    'tavplans.app',
    'tavplans.com',
    'emergentagent.com',
    'localhost',
    '127.0.0.1'
  ];
  
  return !mainDomains.some(domain => hostname.includes(domain));
};

export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState(defaultBranding);
  const [loading, setLoading] = useState(true);

  const resolveTenant = useCallback(async () => {
    try {
      setLoading(true);
      const hostname = window.location.hostname;
      
      let params = {};
      
      // Check for custom domain first
      if (isCustomDomain(hostname)) {
        params.domain = hostname;
      } else {
        // Check for subdomain
        const subdomain = extractSubdomain(hostname);
        if (subdomain) {
          params.subdomain = subdomain;
        }
      }
      
      // If no subdomain or custom domain, use default branding
      if (Object.keys(params).length === 0) {
        setBranding(defaultBranding);
        setLoading(false);
        return;
      }
      
      // Fetch tenant branding
      const response = await axios.get(`${API}/api/tenant/resolve`, { params });
      const tenantBranding = response.data;
      
      setBranding(tenantBranding);
      
      // Apply branding to document
      applyBranding(tenantBranding);
      
    } catch (error) {
      console.error('Error resolving tenant:', error);
      setBranding(defaultBranding);
    } finally {
      setLoading(false);
    }
  }, []);

  const applyBranding = (brandingConfig) => {
    const root = document.documentElement;
    
    // Apply CSS custom properties for colors
    root.style.setProperty('--brand-primary', brandingConfig.primary_color);
    root.style.setProperty('--brand-secondary', brandingConfig.secondary_color);
    root.style.setProperty('--brand-accent', brandingConfig.accent_color);
    
    // Apply font family
    if (brandingConfig.font_family) {
      root.style.setProperty('--brand-font', brandingConfig.font_family);
    }
    
    // Update page title
    if (brandingConfig.company_name && brandingConfig.is_whitelabeled) {
      document.title = brandingConfig.company_name;
    }
    
    // Update favicon
    if (brandingConfig.favicon_url) {
      const existingFavicon = document.querySelector('link[rel="icon"]');
      if (existingFavicon) {
        existingFavicon.href = brandingConfig.favicon_url;
      } else {
        const favicon = document.createElement('link');
        favicon.rel = 'icon';
        favicon.href = brandingConfig.favicon_url;
        document.head.appendChild(favicon);
      }
    }
    
    // Inject custom CSS if provided
    if (brandingConfig.custom_css) {
      let styleElement = document.getElementById('tenant-custom-css');
      if (!styleElement) {
        styleElement = document.createElement('style');
        styleElement.id = 'tenant-custom-css';
        document.head.appendChild(styleElement);
      }
      styleElement.textContent = brandingConfig.custom_css;
    }
  };

  useEffect(() => {
    resolveTenant();
  }, [resolveTenant]);

  const value = {
    branding,
    loading,
    isWhitelabeled: branding.is_whitelabeled,
    refreshBranding: resolveTenant
  };

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
};

export default BrandingContext;
